// Settings › AI assistants — every retailer-facing string, as pure functions so Jest can test them.
// Approved copy: spec dashboard/buyer-insights-chatgpt-connector.md §6.2. The steps are the source for the
// retailer guide (docs/onboarding/HANDOFF_AI_ASSISTANTS_AND_MAKE_GUIDE.md): a test checks the guide uses
// the same words. In a step, **bold** is a button or field name, `code` is text to type, {url} the MCP URL.

export const MCP_URL =
  process.env.REACT_APP_HANDOFF_MCP_URL || 'https://handoff-mcp-374341899949.europe-west4.run.app/mcp';
const API_BASE =
  process.env.REACT_APP_API_BASE_URL || 'https://retailer-dashboard-374341899949.europe-west4.run.app/api/retailer/v1';

export const INTRO =
  'Ask ChatGPT or Claude about your HandOff data: which sizes are still wanted, where you sell out faster ' +
  'than your partners, and which orders you sent and received. They can only read. They cannot change anything.';

export const CHATGPT_STEPS = [
  'Open chatgpt.com and click **Plugins** in the sidebar. Click **Add**, then **Add custom MCP server**.',
  'Name: `HandOff`. Under **Connection**, keep **Server URL** and paste: {url}',
  'Authentication: **OAuth**. Leave **Advanced OAuth settings** as they are. ChatGPT fills them in.',
  'Tick **I understand and want to continue**. Click **Create**.',
  'Click **Connect HandOff**. Your HandOff dashboard opens. Log in if needed, then click **Allow**.',
  'In a new chat, turn HandOff on.',
];
export const CHATGPT_NOTES = [
  'Does ChatGPT say an app with this name already exists? Then HandOff is still there. Open it in **Plugins** and click **Connect**.',
  'ChatGPT Plus can do this. On ChatGPT Business, your workspace admin must allow it first.',
];

// Step 3 is shown as a warning: Claude picks "No sign-in" by itself, which is wrong for HandOff.
export const CLAUDE_WARNING_STEP = 2;
export const CLAUDE_STEPS = [
  'In Claude, open **Settings → Connectors**. Click **Add custom connector**.',
  'Name: `HandOff`. URL: {url}. Click **Add**.',
  "Under **Authentication**, choose **Sign in now**. Claude suggests \"No sign-in\": change it. Under **OAuth client**, keep **Use Claude's published identity**. Leave **Request headers** empty. Click **Add**.",
  'Click **Connect**. In the Claude app on your computer, first click **Continue connecting**. Your HandOff dashboard opens. Log in if needed, then click **Allow**.',
  'Open the HandOff connector. Set **Read-only tools** to **Always allow**. HandOff only reads, so Claude does not need to ask you each time.',
  'In a new chat, turn the HandOff connector on.',
];

export function tryQuestions(brand) {
  const b = brand ? brand.replace(/\b\w/g, (c) => c.toUpperCase()) : null;
  return [
    b ? `Which sizes of ${b} are still wanted?` : 'Which sizes are still wanted?',
    'For which brands am I sold out more often than my partners?',
    'Show my commission items for last month, sent and received, with the declined ones and why.',
    b ? `Give me the buying meeting brief for ${b}.` : 'Give me the buying meeting brief for my biggest brand.',
  ];
}
export const TRY_NOTE = 'The facts come from HandOff. The advice comes from your assistant. HandOff never sees your chat.';

export const MAKE = {
  intro: 'Make needs a key. Only admin users can create one.',
  create: 'Create Make key',
  onlyAdmins: 'Only admin users can do this.',
  shownOnce: 'Copy it now. You will not see it again. Treat it like a password.',
  keyHow: 'In Make, use an HTTP module. Put the key in the header `Authorization` as `Bearer` and your key.',
  recipes: [
    { title: 'Sizes still wanted, every Monday', url: `${API_BASE}/reports/still-wanted`, params: 'format = csv' },
    { title: 'All commission items, every month (CSV)', url: `${API_BASE}/reports/commission-items`, params: 'format = csv' },
  ],
};

export const HELP =
  'Stuck? Email info@handoffleads.com and we set it up with you in 20 minutes. ' +
  'No login yet for a colleague? Email us and we make one.';

const STOPPED = {
  idle: 'Blocked: not used for 90 days',
  replaced: 'Replaced by a new connection',
  user: 'Blocked',
  admin: 'Blocked by HandOff',
};
export const stoppedText = (reason) => STOPPED[reason] || 'Blocked';

const time = (d) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

// "Mon 07:02 via ChatGPT" within a week, else "3 Oct via Make"; "Never" when unused.
export function lastUsed(iso, via, now = new Date()) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const recent = now - d < 6 * 24 * 3600 * 1000;
  const when = recent
    ? `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${time(d)}`
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const name = { chatgpt: 'ChatGPT', claude: 'Claude', make: 'Make', mcp: 'an AI assistant' }[via];
  return name ? `${when} via ${name}` : when;
}

export const madeOn = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export const blockQuestion = (label) => `Block this key? ${label} stops working at once.`;

// Active keys first (newest first), then keys stopped in the last 30 days; older stopped keys are hidden.
export function visibleKeys(keys, now = new Date()) {
  const active = keys.filter((k) => !k.revoked_at);
  const stopped = keys.filter((k) => k.revoked_at && now - new Date(k.revoked_at) < 30 * 24 * 3600 * 1000);
  return { active, stopped };
}

// Splits a step into parts for rendering: {text}, {bold}, {code} or {url: true}.
export function stepParts(step) {
  return step
    .split(/(\*\*[^*]+\*\*|`[^`]+`|\{url\})/)
    .filter(Boolean)
    .map((p) => {
      if (p === '{url}') return { url: true };
      if (p.startsWith('**')) return { bold: p.slice(2, -2) };
      if (p.startsWith('`')) return { code: p.slice(1, -1) };
      return { text: p };
    });
}
